export const adminArticles = [
    {
        id: 'admin.overview',
        title: 'Admin Portal Overview',
        category: 'Platform Admin',
        order: 1,
        summary: 'Platform administration for super admins',
        content: `
The Admin Portal is available to platform super administrators. It provides tools for managing tenants, subscriptions, billing, and system health.

**Access:** Click your profile icon and select **Platform Admin** (only visible to super admins).

**Admin Sections:**

| Section | Purpose |
|---------|---------|
| **Dashboard** | Platform overview, tenant stats, system health |
| **Leads & Customers** | Manage sign-ups, trial tenants, and customer accounts |
| **Subscriptions** | Trials, paid plans and complimentary licences |
| **Usage Insights** | Per-workspace activity — users, last activity, lead status |
| **License Plans** | Configure available subscription plans |
| **Coupons** | Create and manage discount codes |
| **Feedback Reports** | Bug reports and suggestions sent from inside the app |
| **Infrastructure** | Server health, storage per workspace, document integrity, the **Recent admin actions** trail and the Content Security Policy report tally (\`GET /api/admin/csp-report\`) |

**Owner.** The person who starts a workspace is its **owner**: the sign-up, or the contact who accepts the owner invitation sent when you provision from a lead. Use *Transfer ownership* on the lead to hand it to someone else.

**Who can change what.** Every change made through the portal — provisioning, roles, invitations, subscription actions, plans, coupons, key minting, deletions — needs a super admin **signed in**. An admin-flagged API key (the MCP admin tools) can only read: list tenants, usage, stats. Every successful change is recorded with who, what, when and from where; see **Infrastructure → Recent admin actions**.
    `,
        expandableSections: [
            {
                title: 'Accessing the Admin Portal',
                content: `
1. Log in as a super admin user
2. Click your profile icon in the top-right header
3. Select **Platform Admin**
4. The admin portal opens in a separate layout

**Note:** The admin portal operates outside of tenant context. You can manage all tenants from this single interface.

To return to the main app, use the **Back to App** button in the header.
        `,
            },
            {
                title: 'Workspace members, invitations and deletion',
                content: `
From a lead's detail view you can manage the workspace's members:

- **Invite** sends the same invitation email the workspace itself sends (seven-day link). Roles: admin, manager, estimator, user, viewer. If email is not configured the link is shown to you to pass on.
- **Change role** accepts owner plus the workspace roles. Promoting someone to owner demotes the previous owner to admin.
- **Remove** offboards the person the way the workspace does: their sessions for that workspace end and API keys they created are revoked. The owner, and the last admin, cannot be removed — promote someone else first.
- **Delete workspace** is irreversible: the schema is dropped and the tenant, its members' links, sessions and subscription are removed. You must type the workspace **slug** to confirm; the deletion is recorded in Recent admin actions before it runs. Files in object storage are not removed by this step.

**Automatic cleanup.** A nightly job warns the owner or admin of a cancelled workspace seven days before deletion and deletes it 90 days after cancellation — only if that warning was actually sent, at most three workspaces per run. Expired trials without a cancellation date are never touched by the job.
        `,
            },
        ],
        keywords: ['admin', 'platform', 'super admin', 'management', 'portal'],
        route: '/admin',
        relatedArticles: ['admin.leads', 'admin.subscriptions', 'admin.plans'],
    },
    {
        id: 'admin.leads',
        title: 'Leads & Customers',
        category: 'Platform Admin',
        order: 2,
        summary: 'Sign-ups, trials and customer workspaces',
        content: `
**Leads & Customers** lists every lead: self-service sign-ups, contact-form enquiries and leads you add. Click a row to open the detail panel (Details, Onboarding, Billing, Notes, Users, Extra).

**Lead status** is one of: \`lead\` (new), \`pending_verification\` (signed up, email not yet verified), \`qualified\`, \`trial\`, \`follow_up\`, \`active\`, \`suspended\`, \`cancelled\`, \`deleted\`. The system sets \`pending_verification\` at sign-up, \`trial\` when the email is verified and the workspace is created, and \`deleted\` when a workspace is removed. Anything else is refused.

**Toolbar:** filter by salesperson, **Salespeople** (who appears in the assignment list), **Clean up** (previews, then soft-deletes sign-ups that never verified and were never provisioned), **Export**, **Add Lead**. Selecting rows enables bulk status change and bulk assignment, each behind a confirmation. Lists show up to 500 rows.
    `,
        expandableSections: [
            {
                title: 'Lead detail actions',
                content: `
Every action that has a side effect asks first:

- **Provision workspace** — creates the workspace, its database schema and a trial, then emails the **owner invitation** to the lead's contact email in the same step (you are asked for an address if the lead has none). The customer becomes the owner when they accept. If the email cannot be sent, the portal shows the invitation link to share instead.
- **Resend verification** — emails a new link to a sign-up still in \`pending_verification\`; the previous link stops working.
- **Extend trial** — 7, 14 or 30 days (1–90 through the API), at most twice per subscription.
- **Quick checkout** — opens a Stripe checkout for the lead's email. The banner says whether Stripe is in **test** or **LIVE** mode; a live checkout takes a real payment.
- **Apply coupon** — uses one redemption and, when the customer already has a Stripe subscription, discounts it in Stripe at once; otherwise it is recorded for their next checkout. Revoke gives the redemption back.
- **Training** — adjust hours (−100…100, with a reason) or grant the one free hour.
- **Test access** — a flag on the lead only; it changes nothing in the workspace.
- **Unsubscribed / Requested deletion** — saved only after you confirm; clearing "Unsubscribed" is something to do at the customer's request.
- **Users tab** — invite (workspace roles: admin, manager, estimator, user, viewer), change role (owner plus those), remove (ends their sessions and revokes their keys; the last admin cannot be removed), transfer ownership.
- **Delete lead** — a soft delete; support can restore it. **Delete workspace** is irreversible and asks for the slug.
        `,
            },
            {
                title: 'Notes and URL previews',
                content: `
Notes take rich text. Pasting a link fetches a preview card from the public internet only (private and local addresses are refused, and the card is escaped before it is shown). Pasted images are stored inside the note; a note is limited to 64 KB, so attach large screenshots as documents instead.
        `,
            },
        ],
        keywords: ['leads', 'customers', 'sign-up', 'trial', 'provision', 'onboarding', 'coupon', 'training'],
        route: '/admin',
        relatedArticles: ['admin.subscriptions', 'admin.coupons'],
    },
    {
        id: 'admin.subscriptions',
        title: 'Subscriptions',
        category: 'Platform Admin',
        order: 3,
        summary: 'Trials, paid plans and complimentary licences',
        content: `
**Subscriptions** lists one row per workspace with its plan, status, seats and trial end. Statuses: \`trialing\`, \`active\`, \`past_due\`, \`trial_expired\` (read-only workspace), \`cancelled\`, \`incomplete\`, \`paused\`. A trial shows "Trial (no plan)" until the customer subscribes.

**What you can do here:** grant or revoke a **complimentary licence** (sets the subscription active with no price, so it never counts as revenue; an expiry date on the comp is enforced nightly). Plan changes, trial extensions and coupons are done from the lead's detail panel. Cancelling and invoices are done in Stripe.

**Trial lifecycle (automatic):** drip emails on days 1, 3, 7 and 10, warnings 3, 2 and 1 days before the end, then \`trial_expired\`. Emails go to the workspace owner (or its first admin) and honour "Unsubscribed". A cancelled workspace's data is deleted 90 days after cancellation, only after a warning email seven days earlier.
    `,
        keywords: ['subscriptions', 'billing', 'trial', 'comp', 'complimentary', 'expired'],
        route: '/admin',
        relatedArticles: ['admin.plans', 'admin.leads'],
    },
    {
        id: 'admin.plans',
        title: 'License Plans',
        category: 'Platform Admin',
        order: 4,
        summary: 'Plans, prices and the Stripe products behind them',
        content: `
Plans (Free, Core, Pro today) carry a tier, a storage allowance and a feature list. Each plan has up to one **active** monthly and one annual price.

- **Setup Stripe** creates the Stripe products and prices from the local plans (Costed is the source; Stripe mirrors it). **Sync from Stripe** pulls amounts and active flags back for prices that already have a Stripe id.
- **Add Price** creates a price locally and in Stripe. A Stripe price can never be edited: to change an amount, **retire** the old price and add the new one. Retired prices keep billing existing subscribers and are hidden from new checkouts.
- Plan edits (features, storage, active) apply to every subscriber of that plan and are recorded in the admin audit trail.
- **Outside production, Stripe only works with a test-mode key.** A live key on staging is refused, so nothing clicked on staging can reach the real Stripe account.
    `,
        keywords: ['plans', 'pricing', 'subscription', 'license', 'stripe', 'price'],
        route: '/admin',
        relatedArticles: ['admin.subscriptions'],
    },
    {
        id: 'admin.coupons',
        title: 'Coupons',
        category: 'Platform Admin',
        order: 5,
        summary: 'Discount codes for subscriptions',
        content: `
A coupon is **percent off** (1–100), **amount off** or **extra months**, with an optional expiry, a maximum number of redemptions and a redemption window (hours after it is applied during which the discount stands). Codes are letters, digits, dashes and underscores. Every coupon is created in Stripe as a one-time discount that applies to subscription products.

Customers enter the code at checkout. From a lead's Billing tab you can apply a coupon yourself: it uses a redemption and, if they already have a Stripe subscription, discounts it immediately; revoking gives the redemption back and removes the discount. There is no per-customer limit and no plan restriction.
    `,
        keywords: ['coupons', 'discounts', 'promo', 'marketing', 'promotions'],
        route: '/admin',
        relatedArticles: ['admin.leads', 'admin.plans'],
    },
    {
        id: 'admin.feedback',
        title: 'Feedback Reports',
        category: 'Platform Admin',
        order: 6,
        summary: 'Bug reports and suggestions sent from inside the app',
        content: `
Any signed-in person can send a report (bug, feature, question, other) with an optional screenshot. Here you set its status (open, in progress, resolved, closed), write **admin notes** and, if it is linked to GitHub, follow the issue.

**Share** creates a public link that shows the report and its screenshot for **30 days**; your admin notes are never included. Sharing again extends the link; **Revoke** ends it early.
    `,
        keywords: ['feedback', 'bug', 'report', 'share'],
        route: '/admin',
        relatedArticles: ['admin.overview'],
    },
    {
        id: 'admin.devtools',
        title: 'Infrastructure & Dev Tools',
        category: 'Platform Admin',
        order: 7,
        summary: 'Server health, storage, the audit trail and local-only tools',
        content: `
**Infrastructure** shows CPU, memory and disk, the API processes, storage used per workspace against its allowance, the nightly document-integrity sweep and **Recent admin actions** — every change made through this portal with who, when, what was sent and from where. The Content Security Policy report tally is at \`GET /api/admin/csp-report\`.

**Server Controls** appears only in a local development build; there is nothing to restart from the portal on staging or production.
    `,
        keywords: ['dev tools', 'infrastructure', 'audit', 'server', 'storage'],
        route: '/admin',
        relatedArticles: ['admin.overview'],
    },
];
